"use client";

import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage } from "@/components/ui/form";
import { Loader } from "lucide-react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function ToolInterface() {
  const {
    ref: leftColRef,
    isVisible: leftColVisible
  } = useRevealOnScroll({
    threshold: 0.15
  });
  const {
    ref: rightColRef,
    isVisible: rightColVisible
  } = useRevealOnScroll({
    threshold: 0.15,
    once: true
  });
  return <section id="tool-interface" className="dark bg-background text-foreground relative min-h-screen py-16 px-4 md:px-8 lg:px-16 overflow-hidden">
      {}
      <div className="absolute inset-0 bg-[url(https://wpvc-images.s3.us-east-1.amazonaws.com/images/1542868/img/blurred_french_landscape_tool.png)] bg-cover bg-center opacity-30" aria-hidden="true" />
      
      <div className="relative z-10 max-w-7xl mx-auto">
        <div className="grid md:grid-cols-[1fr_1.2fr] gap-8 lg:gap-16 items-start">
          {}
          <div ref={leftColRef} className={`space-y-6 transition-all duration-150 ${leftColVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-5'}`}>
            <h1 className="font-display font-semibold text-4xl md:text-5xl lg:text-6xl leading-tight tracking-tight text-dark-foreground">
              Compare Prices.<br />Travel Smarter.
            </h1>
            
            <div className="space-y-4 text-dark-foreground/90 leading-relaxed">
              <p className="text-lg md:text-xl font-sans">
                Find the cheapest way to travel across France—train, bus, or carshare.
              </p>
              
              <p className="text-base md:text-lg font-serif opacity-80">
                Enter your departure and destination cities, then let our tool calculate and compare real-time pricing from major French transport providers.
              </p>
              
              <p className="text-sm md:text-base font-serif opacity-70">
                No hidden fees. No guesswork. Just transparent, budget-friendly travel options at your fingertips.
              </p>
            </div>
          </div>

          {}
          <div ref={rightColRef} className={`transition-all duration-150 ${rightColVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-5'}`}>
            <FormProvider className="hover:shadow-medium transition-all duration-medium" formId="price-comparison-tool-interface" sectionName="tool-interface" defaultValues={{
            departure: "",
            destination: "",
            travelDate: ""
          }} validationRules={{
            departure: {
              required: true,
              minLength: 2
            },
            destination: {
              required: true,
              minLength: 2
            },
            travelDate: {
              required: true
            }
          }} submitText="Compare Prices" successMessage="Price comparison results are ready!">
              <PriceComparisonForm />
            </FormProvider>
          </div>
        </div>
      </div>
    </section>;
}
function PriceComparisonForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <Card className="bg-white/95 backdrop-blur-sm border border-muted/20 shadow-soft">
        <CardContent className="p-8">
          <div className="text-center space-y-4">
            <div className="size-16 mx-auto bg-accent/10 rounded-full flex items-center justify-center">
              <svg className="size-8 text-accent" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <p className="text-lg font-semibold text-foreground">{successMessage}</p>
            <Button onClick={resetForm} variant="outline" className="mt-4">
              New Comparison
            </Button>
          </div>
        </CardContent>
      </Card>;
  }
  return <Card className="bg-white/95 backdrop-blur-sm border border-muted/20 shadow-soft hover:shadow-medium transition-all duration-medium">
      <CardContent className="p-6 md:p-8">
        <Form onSubmit={handleSubmit}>
          <div className="space-y-5">
            <FormField control={control} name="departure" rules={{
            required: "Departure city is required",
            minLength: {
              value: 2,
              message: "Minimum 2 characters"
            }
          }} render={({
            field
          }) => <FormItem>
                  <FormLabel className="text-foreground font-sans font-medium">Departure City</FormLabel>
                  <FormControl>
                    <Input placeholder="e.g., Paris" className="border-2 border-muted focus:border-accent transition-colors duration-fast min-h-[44px]" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>} />

            <FormField control={control} name="destination" rules={{
            required: "Destination city is required",
            minLength: {
              value: 2,
              message: "Minimum 2 characters"
            }
          }} render={({
            field
          }) => <FormItem>
                  <FormLabel className="text-foreground font-sans font-medium">Destination City</FormLabel>
                  <FormControl>
                    <Input placeholder="e.g., Lyon" className="border-2 border-muted focus:border-accent transition-colors duration-fast min-h-[44px]" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>} />

            <FormField control={control} name="travelDate" rules={{
            required: "Travel date is required"
          }} render={({
            field
          }) => <FormItem>
                  <FormLabel className="text-foreground font-sans font-medium">Travel Date</FormLabel>
                  <FormControl>
                    <Input type="date" className="border-2 border-muted focus:border-accent transition-colors duration-fast min-h-[44px]" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>} />

            {submitError && <div className="p-4 bg-red-50 border border-red-200 rounded-[var(--radius-sm)]">
                <p className="text-red-800 text-sm">{submitError}</p>
              </div>}

            <Button type="submit" disabled={isSubmitting} className="w-full btn-primary hover:scale-[1.03] active:scale-100 transition-all duration-fast">
              {isSubmitting ? <>
                  <Loader className="animate-spin mr-2 size-4" />
                  Searching...
                </> : 'Compare Prices'}
            </Button>
          </div>
        </Form>
      </CardContent>
    </Card>;
}